/**
 * InviteResultDialog.tsx — Modal unificado de resultado de convite/ativação
 *
 * Link + status de e-mail + QR Code para o convidado presente escanear.
 * Usado em Responsáveis, Equipe, Professores, Escolas, etc.
 *
 * @internal true
 * @owner components/invite
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2, Copy, Check, Send, Mail, MailX, X, Link as LinkIcon, QrCode, Smartphone,
} from 'lucide-react';
import {
  normalizeActivationLink,
  buildWhatsAppUrl,
  copyActivationLink,
  PERFIL_LABELS,
  formatInviteRole,
} from './invite-utils';
import { StyledQrCode } from '../../security/qr/StyledQrCode';
import './invite.css';

export type InviteDialogKind = 'ativacao' | 'recuperacao';

interface InviteResultDialogProps {
  open: boolean;
  nome: string;
  email: string;
  perfil?: string;
  cargo?: string;
  title?: string;
  telefone?: string;
  activationLink: string;
  whatsappLink?: string | null;
  emailSent?: boolean;
  emailReason?: string | null;
  /** ativacao = primeiro acesso (72h); recuperacao = reset senha (15 min) */
  kind?: InviteDialogKind;
  /** Sobrescreve o texto de validade */
  validadeLabel?: string;
  onClose: () => void;
}

export function InviteResultDialog({
  open,
  nome,
  email,
  perfil,
  cargo,
  title,
  telefone,
  activationLink,
  whatsappLink,
  emailSent,
  emailReason,
  kind = 'ativacao',
  validadeLabel,
  onClose,
}: InviteResultDialogProps) {
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  if (!open) return null;

  const normalizedLink = normalizeActivationLink(activationLink);
  const roleLabel = formatInviteRole(cargo, perfil);
  const whatsAppUrl = whatsappLink || buildWhatsAppUrl(nome, normalizedLink, perfil, telefone, cargo);
  const isRecovery = kind === 'recuperacao';
  const validade =
    validadeLabel ||
    (isRecovery ? 'Válido por 15 minutos' : 'Válido por 72 horas');
  const firstName = nome.split(' ').slice(0, 2).join(' ');

  const modalTitle = title || (
    isRecovery
      ? 'Acesso para recuperação'
      : roleLabel && roleLabel !== 'Escola'
        ? `${roleLabel} convidado!`
        : 'Convite gerado com sucesso!'
  );

  const handleCopy = async () => {
    const ok = await copyActivationLink(activationLink, { silent: true });
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className="invite-result-overlay" onClick={handleOverlayClick} data-lab-modal="invite-result">
      <div className="invite-result-card invite-result-card--wide" role="dialog" data-dialog="true">
        <div className="invite-result-header">
          <div className="invite-result-icon">
            <CheckCircle2 size={18} />
          </div>
          <div className="invite-result-header-text">
            <h3 className="invite-result-title">
              {modalTitle}
            </h3>
            <p className="invite-result-subtitle">
              {isRecovery ? (
                <>
                  Envie o link ou peça para <strong>{firstName}</strong> escanear o QR Code
                  e cadastrar uma nova senha.
                </>
              ) : (
                <>
                  Envie o link abaixo para <strong>{firstName}</strong> ativar a conta e definir
                  a senha — ou mostre o QR Code se a pessoa estiver presente.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="invite-result-main">
          <div className="invite-result-body">
            <div className="invite-result-field">
              <span className="invite-result-field-label">Email</span>
              <span className="invite-result-field-value">{email || '—'}</span>
            </div>

            {/* Link Box — Oculto no mobile, visível no desktop */}
            <div className="invite-result-field invite-result-field--link hidden sm:block">
              <span className="invite-result-field-label">
                <LinkIcon size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                {isRecovery ? 'Link de recuperação' : 'Link de ativação'}
              </span>
              <div
                className="invite-result-link-box"
                data-lab-invite-link={normalizedLink}
                onClick={() => void handleCopy()}
                title="Clique para copiar"
              >
                <div className="invite-result-link-text">{normalizedLink}</div>
                <p className="invite-result-link-hint">
                  {validade}. Clique para copiar.
                </p>
              </div>
            </div>

            <div className="invite-result-tips">
              <p className="invite-result-tips-title">Orientações</p>
              <ul>
                <li>
                  <Smartphone size={12} /> Se a pessoa estiver na escola, peça para escanear o QR Code
                  com a câmera do celular.
                </li>
                <li>
                  <Mail size={12} /> Prefira e-mail ou WhatsApp quando ela não estiver presente.
                </li>
                <li>
                  <QrCode size={12} /> Após abrir o link, ela define a senha e passa a acessar o app.
                </li>
              </ul>
            </div>

            {emailSent !== undefined && (
              <div
                className={`invite-result-email-badge ${
                  emailSent
                    ? 'invite-result-email-badge--success'
                    : 'invite-result-email-badge--fail'
                }`}
              >
                {emailSent ? (
                  <>
                    <Mail size={12} /> Mensagem enviada para o e-mail {email}
                  </>
                ) : (
                  <>
                    <MailX size={12} /> E-mail não enviado
                    {emailReason ? ` — ${emailReason}` : ''}. Use o link, WhatsApp ou QR Code.
                  </>
                )}
              </div>
            )}
          </div>

          <aside className="invite-result-qr-col" aria-label="QR Code do link">
            <div className="invite-result-qr-frame">
              <StyledQrCode
                value={normalizedLink}
                size={isMobile ? 190 : 300}
                width={isMobile ? 190 : 300}
                height={isMobile ? 190 : 300}
                icon="logo-shield-qrcode-backing"
                iconSize={isMobile ? 42 : 68}
                shape="square"
                style="rounded"
                foreground="#1e293b"
                background="transparent"
                margin={0}
              />
            </div>
          </aside>
        </div>

        <div className="invite-result-footer">
          <button
            type="button"
            className="invite-result-btn invite-result-btn--copy"
            data-testid="btn-copy-invite-link"
            data-lab-action="copy-invite"
            data-lab-invite-link={normalizedLink}
            onClick={() => void handleCopy()}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copiado!' : 'Copiar Link'}
          </button>

          <a
            href={whatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="invite-result-btn invite-result-btn--whatsapp"
          >
            <Send size={14} /> WhatsApp
          </a>

          <button
            type="button"
            data-testid="btn-close-invite-result"
            className="invite-result-btn invite-result-btn--close"
            onClick={onClose}
          >
            <X size={14} /> Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function useInviteDialog() {
  const [state, setState] = useState<(InviteResultDialogProps & { open: true }) | null>(null);

  const show = (props: Omit<InviteResultDialogProps, 'open' | 'onClose'>) => {
    setState({
      ...props,
      open: true,
      onClose: () => setState(null),
    } as InviteResultDialogProps & { open: true });
  };

  const hide = () => setState(null);

  const Dialog = () => (state ? <InviteResultDialog {...state} /> : null);

  return { show, hide, Dialog, isOpen: !!state };
}
